import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, DollarSign, Sparkles, Zap, MessageSquare, ShoppingBag, CheckCircle2 } from "lucide-react";

const STAGE_META = {
  Comment: { color: "from-blue-400 to-cyan-500", Icon: MessageSquare },
  "Story view": { color: "from-pink-400 to-fuchsia-500", Icon: Activity },
  Register: { color: "from-fuchsia-400 to-violet-500", Icon: Activity },
  "AI DM": { color: "from-violet-500 to-fuchsia-500", Icon: Sparkles },
  "AI reply": { color: "from-violet-500 to-fuchsia-500", Icon: Sparkles },
  Attend: { color: "from-violet-500 to-fuchsia-500", Icon: Activity },
  Qualify: { color: "from-violet-500 to-purple-500", Icon: Zap },
  Replay: { color: "from-pink-400 to-rose-500", Icon: Activity },
  Offer: { color: "from-amber-400 to-orange-500", Icon: ShoppingBag },
  Link: { color: "from-amber-400 to-orange-500", Icon: ShoppingBag },
  Upsell: { color: "from-amber-400 to-orange-500", Icon: ShoppingBag },
  Recover: { color: "from-orange-400 to-rose-500", Icon: Zap },
  Payment: { color: "from-emerald-500 to-teal-600", Icon: CheckCircle2 },
};

export default function Flows() {
  const [flows, setFlows] = useState([]);
  const [pulses, setPulses] = useState([]);            // moving leads
  const [popups, setPopups] = useState([]);            // payment success animations
  const flowRefs = useRef([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/flows");
      setFlows(data);
    })();
  }, []);

  // Spawn animated leads moving across each flow
  useEffect(() => {
    if (!flows.length) return;
    const id = setInterval(() => {
      const flowIdx = Math.floor(Math.random() * flows.length);
      const flow = flows[flowIdx];
      const leadId = `pulse_${Date.now()}_${Math.random()}`;
      setPulses((p) => [...p, { id: leadId, flowIdx, stages: flow.steps.length }]);
      // Auto-cleanup
      setTimeout(() => setPulses((p) => p.filter((x) => x.id !== leadId)), flow.steps.length * 1100 + 500);

      // 30% chance of payment success popup
      if (Math.random() < 0.32) {
        const amt = [29, 49, 99, 149, 249][Math.floor(Math.random() * 5)];
        const popId = `pop_${Date.now()}_${Math.random()}`;
        setPopups((p) => [...p, { id: popId, amount: amt, flowIdx }]);
        setTimeout(() => setPopups((p) => p.filter((x) => x.id !== popId)), 3500);
      }
    }, 1700);
    return () => clearInterval(id);
  }, [flows]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-4 sm:p-6 lg:p-10" data-testid="flows-page">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-emerald-500 grid place-items-center shadow-sm">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">Revenue Flows</h1>
            <p className="text-sm text-meta mt-0.5 inline-flex items-center gap-2">
              <span className="pulse-dot" /> AI is actively converting conversations into revenue
            </p>
          </div>
        </div>
        <span className="hidden sm:inline-flex pill pill-ai">
          <Sparkles className="h-3 w-3" /> Realtime
        </span>
      </div>

      <div className="space-y-4">
        {flows.map((f, fi) => (
          <FlowCard key={f.id} flow={f} flowIdx={fi}
            pulses={pulses.filter(p => p.flowIdx === fi)}
            popups={popups.filter(p => p.flowIdx === fi)} />
        ))}
      </div>
    </div>
  );
}

function FlowCard({ flow, flowIdx, pulses, popups }) {
  return (
    <div className="surface p-5 lg:p-6 relative overflow-hidden" data-testid={`flow-${flow.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold text-ink inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-ai" />
            {flow.name}
          </h3>
          <p className="text-xs text-meta mt-1 inline-flex items-center gap-1.5"><span className="pulse-dot" /> Live · AI processing autonomously</p>
        </div>
        <div className="flex items-center gap-6 sm:gap-8 text-sm">
          <div>
            <p className="text-[10px] text-meta uppercase tracking-wider font-semibold">Conversion</p>
            <p className="font-display text-xl sm:text-2xl font-extrabold text-ai">{(flow.conversion*100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-[10px] text-meta uppercase tracking-wider font-semibold">Revenue 30d</p>
            <p className="font-display text-xl sm:text-2xl font-extrabold text-success inline-flex items-center gap-0.5"><DollarSign className="h-4 w-4" />{Math.round(flow.revenue_30d).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Flow visualization */}
      <div className="relative rounded-2xl border border-violet-100 bg-gradient-to-br from-violet-50/60 via-white to-pink-50/40 p-5 sm:p-6 overflow-hidden">
        {/* Grid backdrop for AI feel */}
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

        <div className="relative flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {flow.steps.map((step, i) => {
            const meta = STAGE_META[step] || { color: "from-violet-500 to-pink-500", Icon: Sparkles };
            const Icon = meta.Icon;
            const isLast = i === flow.steps.length - 1;
            return (
              <React.Fragment key={i}>
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.05 * i }}
                  className="shrink-0 relative"
                >
                  <div className={`relative rounded-2xl bg-white border border-violet-200 shadow-sm px-3 sm:px-4 py-3 min-w-[110px] sm:min-w-[130px] text-center`}>
                    <div className={`absolute -top-2 left-1/2 -translate-x-1/2 h-7 w-7 rounded-xl bg-gradient-to-br ${meta.color} grid place-items-center shadow-sm`}>
                      <Icon className="h-3.5 w-3.5 text-white" />
                    </div>
                    <p className="text-xs sm:text-sm font-bold text-ink mt-2">{step}</p>
                    {/* Live count below stage */}
                    <p className="text-[10px] text-meta font-semibold mt-0.5">
                      <LiveCounter base={Math.floor(50 + Math.random() * 200)} flowIdx={flowIdx} stageIdx={i} />
                    </p>
                    {/* AI processing pulse on AI stages */}
                    {(step.includes("AI") || step === "Qualify") && (
                      <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-violet-500 animate-pulse shadow-md shadow-violet-500/60" />
                    )}
                    {/* Payment stage gets success indicator */}
                    {step === "Payment" && (
                      <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse shadow-md shadow-emerald-500/60" />
                    )}
                  </div>
                </motion.div>
                {!isLast && (
                  <div className="relative h-1 w-8 sm:w-12 bg-zinc-200 rounded-full overflow-hidden shrink-0">
                    <motion.div
                      className="absolute inset-y-0 left-0 w-6 rounded-full bg-gradient-to-r from-violet-500 to-pink-500"
                      initial={{ x: -24 }}
                      animate={{ x: 60 }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: "linear", delay: 0.2 * i + flowIdx * 0.4 }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Animated lead pulses — moving dots showing leads progressing */}
        <AnimatePresence>
          {pulses.map((p) => (
            <motion.div
              key={p.id}
              className="absolute top-1/2 -translate-y-1/2 left-6 h-3 w-3 rounded-full ashra-gradient-bg shadow-lg shadow-violet-500/40 z-10 pointer-events-none"
              initial={{ x: 0, opacity: 0 }}
              animate={{ x: ["0%", "100%", "100%"], opacity: [0, 1, 1, 0] }}
              transition={{ duration: p.stages * 1.1, ease: "easeInOut", times: [0, 0.05, 0.95, 1] }}
              exit={{ opacity: 0 }}
              style={{ width: "calc(100% - 48px)", height: 0 }}
            >
              <span className="block h-3 w-3 rounded-full ashra-gradient-bg shadow-lg shadow-violet-500/50 -translate-y-1/2" />
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Payment success popups */}
        <AnimatePresence>
          {popups.map((pop) => (
            <motion.div
              key={pop.id}
              initial={{ opacity: 0, y: 20, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute right-4 bottom-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2 shadow-lg shadow-emerald-500/30 inline-flex items-center gap-2 z-20"
            >
              <CheckCircle2 className="h-4 w-4 text-white" />
              <p className="text-sm font-bold text-white">+${pop.amount} converted</p>
            </motion.div>
          ))}
        </AnimatePresence>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs">
          <div className="inline-flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-meta font-medium"><span className="pulse-dot blue" /> Active leads flowing</span>
            <span className="inline-flex items-center gap-1.5 text-meta font-medium"><span className="pulse-dot orange" /> AI processing</span>
            <span className="inline-flex items-center gap-1.5 text-meta font-medium"><span className="pulse-dot" /> Conversions</span>
          </div>
          <span className="text-meta-soft italic">AI operating this flow autonomously · 24/7</span>
        </div>
      </div>
    </div>
  );
}

// Counter that ticks up periodically — gives the "live operating system" feel
function LiveCounter({ base }) {
  const [v, setV] = useState(base);
  useEffect(() => {
    const id = setInterval(() => {
      if (Math.random() < 0.4) setV((x) => x + 1);
    }, 2200);
    return () => clearInterval(id);
  }, []);
  return <>{v}</>;
}
